<div class="trd-page">
  <app-project-nav></app-project-nav>

  @if (loading()) {
    <div class="trd-state">
      <mat-progress-spinner mode="indeterminate" diameter="40"></mat-progress-spinner>
    </div>
  } @else if (loadError()) {
    <div class="trd-state trd-state--error tre-error">
      <span data-testid="results-load-error">{{ 'testRunResults.loadError' | translate }}</span>
      <a class="trd-back" data-testid="results-back" [routerLink]="executionsLink">
        {{ 'testRunResults.backToExecutions' | translate }}
        <mat-icon aria-hidden="true">chevron_right</mat-icon>
      </a>
    </div>
  } @else if (state(); as runnerState) {
    <div data-testid="run-results">
      <div class="trd-header">
        <div class="trd-heading">
          <div class="trd-eyebrow">{{ 'testRunResults.eyebrow' | translate }}</div>
          <div class="trd-title-row">
            <h1 class="trd-title">{{ 'testRunResults.title' | translate }}</h1>
            @if (runnerState.runKey) {
              <code class="trd-run-key" data-testid="results-run-key">{{ runnerState.runKey }}</code>
            }
            <span class="trd-art" [class.trd-art--auto]="automated()">
              <mat-icon aria-hidden="true">{{ automated() ? 'settings' : 'manage_accounts' }}</mat-icon>
              {{ (automated() ? 'testResults.detail.automated' : 'testResults.detail.manual') | translate }}
            </span>
          </div>
          <div class="trd-subtitle">
            @if (runnerState.runName) {
              {{ runnerState.runName }} ·
            }
            <span data-testid="results-execution-nr">
              {{ 'testRunResults.executionNr' | translate: { nr: runnerState.nr } }}
            </span>
          </div>
        </div>
        <div class="tre-head-links">
          @if (testResultLinkShown()) {
            <a class="trd-back" data-testid="results-open-test-result" [routerLink]="testResultLink">
              {{ 'testRunResults.openTestResult' | translate }}
              <mat-icon aria-hidden="true">photo_library</mat-icon>
            </a>
          }
          <a class="trd-back" data-testid="results-back" [routerLink]="executionsLink">
            {{ 'testRunResults.backToExecutions' | translate }}
            <mat-icon aria-hidden="true">chevron_right</mat-icon>
          </a>
        </div>
      </div>

      <div class="trd-card trd-summary">
        <div class="trd-summary-main">
          <div class="trd-result-circle trd-result-circle--{{ statusKey(outcome()) }}">
            <mat-icon aria-hidden="true">{{ outcomeIcon() }}</mat-icon>
          </div>
          <div>
            <span class="trd-pill trd-pill--{{ statusKey(outcome()) }}" data-testid="results-outcome">
              <span class="trd-pill-dot"></span>
              {{ 'testResults.result.' + statusKey(outcome()) | translate }}
            </span>
            <div class="trd-passed" data-testid="results-passed">
              {{
                'testResults.detail.summaryPassed'
                  | translate: { passed: runnerState.stepsPassed, total: runnerState.stepsTotal }
              }}
            </div>
          </div>
          <span class="trd-flex"></span>
          <div class="trd-metrics">
            <div class="trd-metric">
              <div class="trd-metric-label">{{ 'testResults.detail.metric.environment' | translate }}</div>
              <div class="trd-metric-value" data-testid="results-environment">{{ environment() }}</div>
            </div>
            <div class="trd-metric">
              <div class="trd-metric-label">{{ 'testResults.detail.metric.duration' | translate }}</div>
              <div class="trd-metric-value" data-testid="results-duration">{{ duration() }}</div>
            </div>
            <div class="trd-metric">
              <div class="trd-metric-label">{{ 'testResults.detail.metric.failed' | translate }}</div>
              <div class="trd-metric-value" data-testid="results-failed">{{ failed() }}</div>
            </div>
            <div class="trd-metric">
              <div class="trd-metric-label">{{ 'testResults.detail.metric.skipped' | translate }}</div>
              <div class="trd-metric-value" data-testid="results-skipped">{{ skipped() }}</div>
            </div>
          </div>
        </div>

        <div class="trd-meta">
          <span class="trd-flex"></span>
          <mat-icon class="trd-frozen-icon" aria-hidden="true">lock</mat-icon>
          <span class="trd-frozen">{{ 'testResults.detail.frozen' | translate }}</span>
        </div>

        <div class="trd-stats">
          <span class="trd-stats-label">{{ 'testResults.detail.stats.label' | translate }}</span>
          <span class="trd-stat trd-stat--total">
            {{ 'testResults.detail.stats.total' | translate }}
            <strong>{{ runnerState.stepsTotal }}</strong>
          </span>
          <span class="trd-stat trd-stat--pass">
            {{ 'testResults.detail.stats.pass' | translate }}
            <strong>{{ runnerState.stepsPassed }}</strong>
          </span>
          <span class="trd-stat trd-stat--fail">
            {{ 'testResults.detail.stats.fail' | translate }}
            <strong>{{ failed() }}</strong>
          </span>
          <span class="trd-stat trd-stat--blocked">
            {{ 'testResults.detail.stats.blocked' | translate }}
            <strong>{{ blocked() }}</strong>
          </span>
          <span class="trd-stat trd-stat--skip">
            {{ 'testResults.detail.stats.skip' | translate }}
            <strong>{{ skipped() }}</strong>
          </span>
        </div>
      </div>

      @if (runnerState.runnerJob; as job) {
        <app-runner-job-card [projectId]="projectId" [runnerJob]="job" [hasStepResults]="hasStepResults()" />
      }

      <h2 class="trd-steps-heading">{{ 'testResults.detail.stepsHeading' | translate }}</h2>

      @for (section of sections(); track section.runnerCase.id) {
        <section class="trd-card tre-case" data-testid="case-section">
          <div class="tre-case-head">
            @if (section.runnerCase.key) {
              <code class="trd-run-key" data-testid="case-key">{{ section.runnerCase.key }}</code>
            }
            <h3 class="tre-case-title" data-testid="case-title">{{ section.runnerCase.title }}</h3>
            @if (section.runnerCase.linkedBugKey) {
              <span class="tre-bug" data-testid="case-bug">
                🐞 {{ 'testRunResults.linkedBug' | translate: { key: section.runnerCase.linkedBugKey } }}
              </span>
            }
            <span class="trd-flex"></span>
            <span class="trd-pill trd-pill--{{ statusKey(section.runnerCase.status) }}" data-testid="case-pill">
              <span class="trd-pill-dot"></span>
              {{ 'testResults.result.' + statusKey(section.runnerCase.status) | translate }}
            </span>
          </div>

          <div class="trd-steps-head">
            <div class="trd-col-nr">{{ 'testResults.detail.table.number' | translate }}</div>
            <div class="trd-col-step">{{ 'testResults.detail.table.step' | translate }}</div>
            <div class="trd-col-duration">{{ 'testResults.detail.table.duration' | translate }}</div>
            <div class="trd-col-result">{{ 'testResults.detail.table.result' | translate }}</div>
          </div>

          @for (entry of section.entries; track entry.step.id) {
            @if (entry.isGroup) {
              <div class="trd-group" data-testid="step-group">
                <mat-icon aria-hidden="true">content_copy</mat-icon>
                <span>{{ entry.step.description }}</span>
              </div>
            } @else {
              <div class="trd-step" data-testid="step-row">
                <div class="trd-col-nr" [class.trd-col-nr--indent]="entry.depth > 0" data-testid="step-nr">
                  {{ entry.number }}
                </div>
                <div class="trd-col-step">
                  <div class="trd-step-text" data-testid="step-text">{{ entry.step.description }}</div>
                  @if (entry.step.expected) {
                    <div class="trd-soll" data-testid="step-expected">
                      <strong>{{ 'testResults.detail.soll' | translate }}</strong>
                      {{ entry.step.expected }}
                    </div>
                  }
                  @if (entry.step.actualResult) {
                    <div
                      class="trd-ist"
                      [class.trd-ist--fail]="entry.step.actualStatus === 'FAILED'"
                      data-testid="step-actual"
                    >
                      <strong>{{ 'testResults.detail.ist' | translate }}</strong>
                      {{ entry.step.actualResult }}
                    </div>
                  }
                  @if (entry.step.note) {
                    <div class="trd-comment" data-testid="step-note">
                      {{ 'testRunResults.note' | translate }} {{ entry.step.note }}
                    </div>
                  }
                  @if (entry.step.linkedBugKey) {
                    <div class="tre-bug tre-bug--step" data-testid="step-bug">
                      🐞 {{ 'testRunResults.linkedBug' | translate: { key: entry.step.linkedBugKey } }}
                    </div>
                  }
                </div>
                <div class="trd-col-duration" data-testid="step-duration">{{ stepDuration(entry.step) }}</div>
                <div class="trd-col-result">
                  <span class="trd-pill trd-pill--{{ statusKey(entry.step.actualStatus) }}" data-testid="step-pill">
                    <span class="trd-pill-dot"></span>
                    {{ 'testResults.result.' + statusKey(entry.step.actualStatus) | translate }}
                  </span>
                </div>
              </div>
            }
          } @empty {
            <div class="trd-empty" data-testid="case-no-steps">{{ 'testRunResults.noSteps' | translate }}</div>
          }
        </section>
      }
    </div>
  }
</div>
